import React, { Component, RefObject } from 'react';
import ReactDOM from 'react-dom';

import { FrameContextProvider } from './Context';
import Content from './Content';

//@ts-ignore
//eslint-disable-next-line
import css from '!!raw-loader!../../styles/index.css';

const IFRAME_INITIAL_CONTENT = `
<!DOCTYPE html>
<html>
  <head>
    <meta name="viewport" content="width=device-width, initial-scale=1">

    <style>
      ${css}
    </style>
  </head>
  <body class="text-sm">
    <div></div>
  </body>
</html>
`;

interface FrameProps {
  style?: React.CSSProperties;
  head?: JSX.Element;
  children?: JSX.Element;
  mountTarget?: string;
  contentDidMount?: () => void;
  contentDidUpdate?: () => void;
  initialContent?: string;
}

interface FrameState {
  iframeLoaded: boolean;
}

// Taken from https://github.com/ryanseddon/react-frame-component
// with fixes for Preact (portal cannot unmmount multiple sibling
// child components)
export default class Frame extends Component<FrameProps, FrameState> {
  private node: HTMLIFrameElement | null = null;
  private _isMounted = false;

  static defaultProps: FrameProps = {
    style: {},
    contentDidMount: () => {},
    contentDidUpdate: () => {},
    initialContent: IFRAME_INITIAL_CONTENT,
  };

  constructor(props: FrameProps) {
    super(props);
    this.state = { iframeLoaded: false };
  }

  componentDidMount() {
    this._isMounted = true;

    const doc = this.getDoc();
    if (doc && doc.readyState === 'complete') {
      this.forceUpdate();
    } else {
      this.node?.addEventListener('load', this.handleLoad);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;

    this.node?.removeEventListener('load', this.handleLoad);
  }

  getDoc(): Document | null {
    return this.node?.contentDocument ?? null;
  }

  getMountTarget(): Element | null {
    const doc = this.getDoc();
    if (this.props.mountTarget) {
      return doc?.querySelector(this.props.mountTarget) ?? null;
    }
    return doc?.body.children[0] ?? null;
  }

  setRef = (node: HTMLIFrameElement | null) => {
    this.node = node;
  };

  handleLoad = () => {
    this.setState({ iframeLoaded: true });
  };

  renderFrameContents(): JSX.Element | null {
    if (!this._isMounted) {
      return null;
    }

    const doc = this.getDoc();

    if (!doc) return null;

    const { contentDidMount, contentDidUpdate } = this.props;

    const win = doc?.defaultView ?? undefined;
    const contents = (
      <Content
        contentDidMount={contentDidMount}
        contentDidUpdate={contentDidUpdate}
      >
        <FrameContextProvider value={{ document: doc, window: win }}>
          <div className="frame-content">{this.props.children}</div>
        </FrameContextProvider>
      </Content>
    );

    const mountTarget = this.getMountTarget();

    if (!mountTarget) return null;

    return ReactDOM.createPortal(contents, mountTarget);
  }

  render() {
    const {
      style,
      head,
      initialContent,
      mountTarget,
      contentDidMount,
      contentDidUpdate,
      children,
      ...iframeProps
    } = this.props;

    return (
      <iframe
        title="gb-frame"
        {...iframeProps}
        style={style}
        ref={this.setRef}
        onLoad={this.handleLoad}
        srcDoc={initialContent}
      >
        {this.state.iframeLoaded && this.renderFrameContents()}
      </iframe>
    );
  }
}
